import React, { useCallback, useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import api from "utils/api";
import { businessPath } from "utils/businessRoutes";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import MenuItem from "components/MenuItem";

const CompanySelector = () => {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const businesses = useSelector((state) => state.user.businesses);
  const userDetails = useSelector((state) => state.user.details);
  const [options, setOptions] = useState(businesses);
  const currentBusiness = useSelector((state) => state.business);
  const [debouncedQuery] = useDebounce(query, 250);
  const selected = currentBusiness?.organizationId ? currentBusiness : businesses?.find(b => b.businessId === currentBusiness?.businessId)
  const account = selected?.organizationId
    ? { organizationId: selected.organizationId, name: selected.accountName }
    : currentBusiness?.businessId ? null : userDetails?.businessAccount;

  const handleSelect = useCallback(
    (e, newValue) => {
      if (newValue) {
        const id = newValue.businessId;
        navigate(businessPath(id, newValue.organizationId));

      }
    },
    [navigate]
  );

  const handleChange = (e, val, reason) => {
    if (reason !== "reset") {
      setQuery(val);
    }
  };

  const searchBusiness = useCallback(async () => {
    const res = await api.get(`/user/businesses?search=${debouncedQuery}`);
    return res.content;
  }, [debouncedQuery]);

  useEffect(() => {
    async function getResults() {
      if (!debouncedQuery) {
        setOptions(businesses);
        return;
      }

      try {
        const newOptions = await searchBusiness();
        setOptions(newOptions);
      } catch (e) {
        console.error(e);
      }
    }
    getResults();
  }, [debouncedQuery, searchBusiness, businesses]);

  return (
    <Box py={3}>
      {account && (
        <Box mb={2}>
          <Typography color="text.secondary" display="block" variant="caption" sx={{ px: 2, mb: 0.75 }}>
            Account
          </Typography>
          <MenuItem
            icon={<BusinessOutlinedIcon sx={{ color: 'black' }} />}
            text={account.name}
            link={currentBusiness?.businessId
              ? businessPath(currentBusiness.businessId, account.organizationId, 'account')
              : `/business-account/${account.organizationId}`}
          />
        </Box>
      )}
      <Box px={2}>
      <Typography color="text.secondary" display="block" mb={0.75} variant="caption">
        Current business
      </Typography>
      <Autocomplete
        disableClearable
        isOptionEqualToValue={(a, b) => a.businessId === b.businessId}
        getOptionLabel={(o) =>
          o.companyInfo ? o.companyInfo.companyName : "No business yet!"
        }
        onInputChange={handleChange}
        onChange={handleSelect}
        options={options}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            inputProps={{
              ...params.inputProps,
              "aria-label": "Current business",
            }}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "secondary.main",
                color: "black",
                "& fieldset": { 
                  border: "1px solid rgba(0, 0, 0, 0.3)" 
                },
                "&:hover fieldset": { borderColor: "black" },
                "&.Mui-focused fieldset": { borderColor: "primary.main" },
              },
              "& .MuiInputLabel-root": { color: "#fff" },
            }}
          />
        )}
        value={currentBusiness}
      />
      </Box>
    </Box>
  );
};

export default CompanySelector;
